import React, { useState, useEffect } from "react";
import styles from "./appointments.module.css";
import PageTitle from "../../components/screens/page-title";
import Table from "../../components/ui/table";
import Input from "../../components/ui/input";
import Button from "../../components/ui/button";
import Dropdown from "../../components/ui/dropdown";
import { MyIcons } from "../../components/icons/my-icons";
import { useNavigate } from "react-router-dom";
import AppointmentDetailModal from "../../components/modals/appointment-detail-modal";
import {
  getCitasPendientesCerrar,
  getProximasCitas,
  getCitasHistorial,
} from "../../services/apiService";
import { showErrorToast, showSuccessToast } from "../../utils/notifications";
import usePageTitle from "../../hooks/usePageTitle";

const AppointmentsPage = () => {
  usePageTitle('Citas');
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const username = localStorage.getItem("username") || "";
  
  // State for appointments data
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [historyAppointments, setHistoryAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for selected filter
  const [selectedFilter, setSelectedFilter] = useState({ value: "pending", label: "Pendientes" });
  
  // State for modal
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter options
  const filterOptions = [
    { value: "pending", label: "Pendientes" },
    { value: "upcoming", label: "Próximas" },
    { value: "history", label: "Historial" },
  ];

  useEffect(() => {
    loadAllAppointments();
  }, []);

  const loadAllAppointments = async () => {
    setLoading(true);
    try {
      const [pending, upcoming, history] = await Promise.all([
        getCitasPendientesCerrar(),
        getProximasCitas(),
        getCitasHistorial(),
      ]);
      setPendingAppointments(pending || []);
      setUpcomingAppointments(upcoming || []);
      setHistoryAppointments(history || []);
    } catch (error) {
      console.error("Error loading appointments:", error);
      showErrorToast("Error al cargar las citas");
    } finally {
      setLoading(false);
    }
  };

  // Get current appointments based on selected filter
  const getCurrentAppointments = () => {
    switch (selectedFilter.value) {
      case "pending":
        return pendingAppointments;
      case "upcoming":
        return upcomingAppointments;
      case "history":
        return historyAppointments;
      default:
        return [];
    }
  };

  const headers = [
    { label: "Expediente", key: "expedienteId" },
    { label: "Dirección", key: "domicilioCliente" },
    { label: "Localidad", key: "localidadCliente" },
    { label: "Fecha Inicio", key: "fechaCita" },
    { label: "Fecha Fin", key: "fechaCitaFin" },
  ];

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const appointments = getCurrentAppointments();
  
  const filteredAppointments = appointments.filter(app => 
    app.expedienteId.toString().includes(searchValue.toLowerCase()) ||
    app.domicilioCliente.toLowerCase().includes(searchValue.toLowerCase()) ||
    app.localidadCliente.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleViewDetails = (appointment, e) => {
    console.log("Viewing details for appointment:", appointment);
    
    // Verificar si es click de rueda del ratón o modificador (Ctrl/Cmd) para abrir en nueva pestaña
    try {
      const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
      const isModifier = !!(e && (e.ctrlKey || e.metaKey));
      
      if ((isMiddle || isModifier) && appointment.expedienteId) {
        // Usar window.open sin parámetros adicionales para comportamiento nativo del navegador
        window.open(`/record/${appointment.expedienteId}`, '_blank');
        return;
      }
    } catch (err) {
      // Fallback to modal
      console.error("Error handling click event:", err);
    }
    
    // Abrir modal normalmente si no es middle-click
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleModalUpdate = () => {
    loadAllAppointments();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No definida";
    const date = new Date(dateString);
    
    // Detectar si es una cita "sin hora" (00:00 o 01:00)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    if ((hours === 0 || hours === 1) && minutes === 0) {
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " - Sin hora";
    }
    
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format data for table display
  const formattedAppointments = filteredAppointments.map(app => ({
    ...app,
    _rawFechaCita: app.fechaCita,
    fechaCita: formatDate(app.fechaCita),
    fechaCitaFin: formatDate(app.fechaCitaFin),
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <PageTitle iconName="calendarFilled" title={`Citas de ${username}`} />
      
      <div className={styles.searchContainer}>
        <Input
          name="search"
          value={searchValue}
          onChange={handleSearch}
          placeholder="Buscar por expediente, dirección o localidad"
          isSearch={true}
        />
      </div>

      <div className={styles.filterContainer}>
        <Dropdown
          label="Filtrar por:"
          options={filterOptions}
          selected={selectedFilter}
          onSelect={setSelectedFilter}
          style={{ minWidth: "200px" }}
          clearable={false}
        />
      </div>
      
      {loading ? (
        <div className={styles.loadingContainer}>
          <p>Cargando citas...</p>
        </div>
      ) : (
        <Table
          headers={headers}
          data={formattedAppointments}
          onPress={handleViewDetails}
          getRowClass={(row, styles) => {
            if (!row._rawFechaCita) return "";
            const d = new Date(row._rawFechaCita);
            const now = new Date();
            if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) {
              return styles.todayRow;
            }
            return "";
          }}
          renderActions={(appointment) => (
            <div className={styles.actionButtons}>
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails(appointment);
                }}
                title="Ver detalles"
              >
                <MyIcons name="eye" width={24} height={24} color={"var(--main)"} />
              </button>
            </div>
          )}
        />
      )}

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <AppointmentDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          // Accept multiple possible id fields from the API (citaId, id, appointmentId)
          citaId={selectedAppointment.citaId || selectedAppointment.id || selectedAppointment.appointmentId}
          onUpdate={handleModalUpdate}
        />
      )}
    </div>
  );
};

export default AppointmentsPage;